import React from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle, ArrowUp } from 'lucide-react';
import { businessInfo, locations } from '../data/mock';
import { trackPixel } from '../lib/pixel';

const Footer = () => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-luxury-green text-white/70">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <span className="font-display text-2xl text-white">Dublin Holistic</span>
              <span className="block text-luxury-gold text-[10px] tracking-[0.3em] uppercase mt-0.5">Massage & Wellness</span>
            </div>
            <p className="text-white/50 leading-relaxed text-sm font-light mb-8">
              Professional therapeutic massage by qualified and experienced therapists. Your wellness is our priority.
            </p>
            <div className="flex gap-3">
              <a href={`https://wa.me/${businessInfo.whatsapp}`} target="_blank" rel="noopener noreferrer"
                onClick={() => trackPixel('Contact', { content_name: 'Footer WhatsApp', method: 'whatsapp' })}
                className="w-10 h-10 bg-white/10 hover:bg-luxury-gold/20 hover:text-luxury-gold rounded-full flex items-center justify-center transition-all" aria-label="WhatsApp">
                <MessageCircle size={18} />
              </a>
              <a href={businessInfo.tiktokUrl} target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-luxury-gold/20 hover:text-luxury-gold rounded-full flex items-center justify-center transition-all" aria-label="TikTok">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/></svg>
              </a>
              <a href={businessInfo.instagramUrl} target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-luxury-gold/20 hover:text-luxury-gold rounded-full flex items-center justify-center transition-all" aria-label="Instagram">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-luxury-gold text-[10px] uppercase tracking-[0.3em] font-medium mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {['Home', 'Services', 'Team', 'Locations', 'Reviews', 'Contact'].map((link) => (
                <li key={link}>
                  <a href={`#${link.toLowerCase()}`}
                    className="text-white/50 hover:text-luxury-gold transition-colors text-sm font-light">
                    {link}
                  </a>
                </li>
              ))}
              <li><a href="/my-bookings" className="text-white/50 hover:text-luxury-gold transition-colors text-sm font-light">My Bookings</a></li>
              <li><a href="/blog" className="text-white/50 hover:text-luxury-gold transition-colors text-sm font-light" data-testid="footer-blog-link">Wellness Blog</a></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-luxury-gold text-[10px] uppercase tracking-[0.3em] font-medium mb-6">Treatments</h4>
            <ul className="space-y-3 text-sm font-light text-white/50">
              <li>Deep Tissue Massage</li>
              <li>Relaxing Massage</li>
              <li>Lomi Lomi</li>
              <li>Reflexology</li>
              <li>Sports Massage</li>
              <li>Couples Massage</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-luxury-gold text-[10px] uppercase tracking-[0.3em] font-medium mb-6">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${businessInfo.phone}`} onClick={() => trackPixel('Contact', { content_name: 'Footer Phone', method: 'phone' })} className="flex items-start gap-3 text-white/50 hover:text-luxury-gold transition-colors text-sm font-light">
                  <Phone size={14} className="mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  {businessInfo.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${businessInfo.email}`} className="flex items-start gap-3 text-white/50 hover:text-luxury-gold transition-colors text-sm font-light">
                  <Mail size={14} className="mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <span className="break-all">{businessInfo.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/50 text-sm font-light">
                <Clock size={14} className="mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                {businessInfo.hours}
              </li>
              {locations.map((loc) => (
                <li key={loc.id} className="flex items-start gap-3 text-white/50 text-sm font-light">
                  <MapPin size={14} className="mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <span className="text-white/70">{loc.name}</span>
                    <br /><span className="text-xs">{loc.address}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/30 text-xs tracking-wide">
            &copy; {currentYear} Dublin Holistic Massage. All rights reserved.
          </p>
          <span className="flex items-center gap-2.5 text-white/30 text-xs tracking-wide">
            <span className="bg-alabaster-100 rounded-full p-1.5 flex items-center justify-center">
              <img
                src="/imta-logo.png"
                alt="IMTA member"
                className="w-8 h-8 object-contain"
                data-testid="footer-imta-logo"
              />
            </span>
            <img
              src="/nrri-logo.png"
              alt="NRRI member"
              className="w-9 h-9 object-contain rounded-full"
              data-testid="footer-nrri-logo"
            />
            <img
              src="/tripadvisor-2026.png"
              alt="Tripadvisor Travellers' Choice 2026"
              className="w-8 h-10 object-contain"
              data-testid="footer-tripadvisor-badge"
            />
            Registered with IMTA &amp; NRRI &middot; Travellers&rsquo; Choice 2026
          </span>
          <button onClick={scrollToTop}
            className="w-10 h-10 bg-white/10 hover:bg-luxury-gold/20 hover:text-luxury-gold rounded-full flex items-center justify-center transition-all"
            aria-label="Scroll to top">
            <ArrowUp size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
